import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('newest');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const categoryArticles = useMemo(() => {
    if (!currentCategory) return [];
    let filtered = articles.filter((a) => a.categoryId === currentCategory.id);
    if (sortBy === 'newest') {
      filtered = [...filtered].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
    }
    return filtered;
  }, [currentCategory, sortBy]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-600">分类不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer no-underline mt-2 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 爱妻艺`,
    description: currentCategory.description,
    url: `https://mhowiub.cn/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://mhowiub.cn/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />

              {/* Category Header */}
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 mb-10">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                    <i className={`${currentCategory.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                      {currentCategory.name}
                    </h1>
                    <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
                      {currentCategory.description}
                    </p>
                    <p className="text-xs text-foreground-400 mt-3">
                      共 {categoryArticles.length} 篇文章
                    </p>
                  </div>
                </div>
              </div>

              {/* Category Tabs */}
              <div className="mb-10">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-nowrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer no-underline ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div className="flex items-center justify-between mb-8">
                <p className="text-sm text-foreground-600">
                  共找到 <strong className="text-foreground-900">{categoryArticles.length}</strong> 篇相关文章
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  <button
                    onClick={() => setSortBy('newest')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer border-none ${
                      sortBy === 'newest'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer border-none ${
                      sortBy === 'popular'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    推荐
                  </button>
                </div>
              </div>

              {/* Articles Grid */}
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      coverImage={article.coverImage}
                      categoryName={article.categoryName}
                      categorySlug={article.categorySlug}
                      author={article.author}
                      publishedAt={article.publishedAt}
                      readTime={article.readTime}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}